import { useCallback } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../../../components/common/Flex';
import EventModalMediaContent from '../../../../components/EventModalMediaContent';
import Loader from '../../../../components/Loader';
import { useGetCustomerDetailsQuery } from '../../../../slices/customersApiSlice';
import AppScrollArea from '../../../../components/common/AppScrollArea';

const CustomerModal = ({ setIsOpenModal, isOpenModal, customerId, customerData = null, onHide }) => {
	const { isDark } = useSelector((state) => state.config);
	const navigate = useNavigate();

	// 如果有customerData且没有customerId，尝试从customerData获取id
	const actualCustomerId = customerId || customerData?.id || customerData?._id;

	const {
		data: customer,
		isLoading,
		isFetching,
	} = useGetCustomerDetailsQuery(actualCustomerId, {
		skip: !actualCustomerId,
	});

	// 使用customerData或查询到的customer
	const displayCustomer = customer || customerData;

	const handleClose = useCallback(() => {
		setIsOpenModal(false);
		if (onHide) {
			onHide();
		}
	}, [setIsOpenModal, onHide]);

	const handleViewDetail = useCallback(() => {
		if (actualCustomerId) {
			setIsOpenModal(false);
			setTimeout(() => {
				navigate('/customerlist', { state: { selectedCustomerId: actualCustomerId } });
			}, 100);
		}
	}, [actualCustomerId, navigate, setIsOpenModal]);

	const isLoadingData = isLoading || isFetching;

	return (
		<Modal show={isOpenModal} onHide={handleClose} contentClassName='border' centered size='xl'>
			<Modal.Header closeButton closeVariant={isDark ? 'white' : undefined} className='px-x1 border-bottom-0 bg-body-tertiary'>
				{isLoadingData && !displayCustomer ? (
					<div className='flex-grow-1' aria-hidden='true' />
				) : (
					<Modal.Title>
						<Flex>
							<h5 className='mb-0'>
								<FontAwesomeIcon icon='building' className='text-success me-2' />
								{displayCustomer?.name || '-'}
							</h5>
						</Flex>

						{displayCustomer?.type && (
							<p className='mb-0 fs-10 mt-1 fw-normal'>
								客户类型：<span className='text-primary'>{displayCustomer.type}</span>
							</p>
						)}
					</Modal.Title>
				)}
			</Modal.Header>
			<Modal.Body className='px-x1 pb-x1 pt-1 fs-10'>
				{isLoadingData && !displayCustomer ? (
					<div className='modal-dots-loading'>
						<Loader />
					</div>
				) : displayCustomer ? (
					<AppScrollArea maxHeight='calc(90vh - 10rem)'>
					<>
						<Row>
							<Col>
								<EventModalMediaContent icon='align-left' heading='基本信息'>
									<div className='fs-10'>
										{displayCustomer?.name && (
											<p className='mb-2'>
												<strong>客户名称：</strong>
												{displayCustomer.name}
											</p>
										)}
										{displayCustomer?.type && (
											<p className='mb-2'>
												<strong>客户类型：</strong>
												{displayCustomer.type}
											</p>
										)}
										{displayCustomer?.city && (
											<p className='mb-2'>
												<strong>城市：</strong>
												{displayCustomer.city}
											</p>
										)}
										{displayCustomer?.address && (
											<p className='mb-2'>
												<strong>详细地址：</strong>
												{displayCustomer.address}
											</p>
										)}
										{displayCustomer?.sale?.name && (
											<p className='mb-2'>
												<strong>销售员：</strong>
												{displayCustomer.sale.name}
											</p>
										)}
									</div>
								</EventModalMediaContent>
							</Col>
							{displayCustomer?.staffs && displayCustomer.staffs.length > 0 && (
								<Col sm={7}>
									<EventModalMediaContent icon='users' heading='联系人信息'>
										<div className='fs-10'>
											{displayCustomer.staffs.map((staff, index) => (
												<div key={staff._id || index} className='mb-3 pb-3 border-bottom'>
													{staff.name && (
														<p className='mb-1'>
															<strong>姓名：</strong>
															{staff.name}
														</p>
													)}
													{staff.phone && (
														<p className='mb-1'>
															<strong>电话：</strong>
															<a href={`tel:${staff.phone}`}>{staff.phone}</a>
														</p>
													)}
													{staff.wechat && (
														<p className='mb-1'>
															<strong>微信：</strong>
															{staff.wechat}
														</p>
													)}
													{staff.email && (
														<p className='mb-1'>
															<strong>邮箱：</strong>
															<a href={`mailto:${staff.email}`}>{staff.email}</a>
														</p>
													)}
												</div>
											))}
										</div>
									</EventModalMediaContent>
								</Col>
							)}
						</Row>
					</>
					</AppScrollArea>
				) : (
					<div className='text-center py-4'>
						<p className='text-muted'>无法加载客户信息</p>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer className='border-top-0'>
				<Button variant='falcon-default' size='sm' onClick={handleClose}>
					关闭
				</Button>
				{actualCustomerId && (
					<Button variant='falcon-primary' size='sm' onClick={handleViewDetail}>
						查看详情
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default CustomerModal;
